import { useState, useEffect } from 'react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Trash2, Check, X, GripVertical } from 'lucide-react';
import { AgendaItem } from '@/types/Meeting';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Only need to define component-specific props interfaces
interface AgendaListProps {
  agenda: AgendaItem[];
  onAddAgendaItem: (newItem: { text: string; completed: boolean }) => void;
  onDeleteAgendaItem: (agendaItemId: string) => void;
  onReorderAgenda: (newAgenda: AgendaItem[]) => void;
  onUpdateAgendaItem: (agendaItemId: string, updatedItem: { text: string; completed: boolean; order: number }) => void;
  variant?: 'card' | 'classic';
}

interface SortableAgendaItemProps {
  item: AgendaItem;
  onDelete: () => void;
  onEdit: () => void;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (value: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  onUpdateAgendaItem: (agendaItemId: string, updatedItem: { text: string; completed: boolean; order: number }) => void;
  variant?: 'card' | 'classic';
}

function SortableAgendaItem({
  item,
  onDelete,
  onEdit,
  isEditing,
  editText,
  onEditTextChange,
  onEditSave,
  onEditCancel,
  onUpdateAgendaItem,
  variant = 'classic',
}: SortableAgendaItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Conditional styling based on variant
  const alignmentClass = variant === 'card' ? 'items-start' : 'items-center';
  const offsetClass = variant === 'card' ? 'mt-0.5' : '';

  return (
    <div ref={setNodeRef} style={style} className={`flex ${alignmentClass} gap-2 px-2 py-1 rounded-lg hover:bg-secondary/50 group`}>
      <button className={`cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground ${offsetClass}`} {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        className={offsetClass}
        checked={item.completed}
        onCheckedChange={(checked) => 
          onUpdateAgendaItem(item._id, { 
            ...item,
            completed: checked === true,
            order: item.order ?? 0 // Handle potentially undefined order
          })
        }
      />
      {isEditing ? (
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Input 
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)} 
              onKeyDown={(e) => e.key === 'Enter' && onEditSave()} 
              className="flex-1" 
              autoFocus 
            />
            <Button variant="ghost" size="icon" onClick={onEditSave}>
              <Check className="h-4 w-4 text-green-500" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onEditCancel}>
              <X className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      ) : (
        <>
          <span className={`text-left flex-1 cursor-pointer hover:text-primary ${item.completed ? 'line-through text-muted-foreground' : ''}`} onClick={onEdit}>
            {item.text}
          </span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-destructive hover:text-destructive/80" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function AgendaList({ agenda, onAddAgendaItem, onDeleteAgendaItem, onReorderAgenda, onUpdateAgendaItem, variant = 'classic' }: AgendaListProps) {
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [localAgenda, setLocalAgenda] = useState<AgendaItem[]>([]);

  useEffect(() => {
    setLocalAgenda([...agenda]);
    console.log("Agenda loaded:", agenda);
  }, [agenda]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAddItem = () => {
    if (newItem.trim()) {
      onAddAgendaItem({ text: newItem.trim(), completed: false });
      setNewItem('');
    }
  };

  const handleDeleteItem = (agendaItemId: string) => {
    onDeleteAgendaItem(agendaItemId);
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditText(localAgenda[index].text);
  };

  const handleEditSave = (agendaItemId: string) => {
    if (editingIndex !== null && editText.trim()) {
      const itemToUpdateIndex = localAgenda.findIndex(item => item._id === agendaItemId);
      if (itemToUpdateIndex !== -1) {
        onUpdateAgendaItem(agendaItemId, {
          text: editText.trim(),
          completed: localAgenda[itemToUpdateIndex].completed,
          order: localAgenda[itemToUpdateIndex].order ?? 0 // Handle potentially undefined order
        });
        setEditingIndex(null);
        setEditText('');
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const updatedAgenda = [...localAgenda];
    const oldIndex = updatedAgenda.findIndex((item) => item._id === active.id);
    const newIndex = updatedAgenda.findIndex((item) => item._id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      console.error("Error finding indexes for drag operation.");
      return;
    }

    const [movedItem] = updatedAgenda.splice(oldIndex, 1);
    updatedAgenda.splice(newIndex, 0, movedItem);

    const reorderedAgenda = updatedAgenda.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setLocalAgenda(reorderedAgenda);
    onReorderAgenda(reorderedAgenda);
  };

  const sortedAgenda = [...localAgenda].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={() => setEditingIndex(null)} onDragEnd={handleDragEnd}>
        <SortableContext items={localAgenda.map((item) => item._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-0.5 bg-background/50 rounded-lg p-2">
            {sortedAgenda.map((item) => (
              <SortableAgendaItem
                key={item._id}
                item={item}
                onDelete={() => handleDeleteItem(item._id)}
                onEdit={() => startEditing(localAgenda.findIndex((agItem) => agItem._id === item._id))}
                isEditing={editingIndex === localAgenda.findIndex((agItem) => agItem._id === item._id)}
                editText={editText}
                onEditTextChange={setEditText}
                onEditSave={() => handleEditSave(item._id)}
                onEditCancel={() => setEditingIndex(null)}
                onUpdateAgendaItem={onUpdateAgendaItem}
                variant={variant}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex gap-2 px-1">
        <Input 
          value={newItem} 
          onChange={(e) => setNewItem(e.target.value)} 
          placeholder="Add agenda item..." 
          onKeyDown={(e) => e.key === 'Enter' && handleAddItem()} 
        />
        <Button onClick={handleAddItem}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default AgendaList;
